import type {
  CaptureManifest,
  CaptureType,
  PendingBugRequest,
} from "./types";

const SESSION_KEY = "debug-capture:session";
const DB_NAME = "debug-capture-drafts";
const DB_VERSION = 1;
const STORE_NAME = "media";
const TTL_MS = 24 * 60 * 60 * 1000;

export interface CaptureStepRecord {
  stepNumber: number;
  captureId: string;
  label: string;
  summary: string;
  captureType: CaptureType;
  hasVideo: boolean;
  savedAt: string;
  manifest: CaptureManifest;
}

export interface DebugCaptureSessionDraft {
  version: 1;
  sessionId: string;
  updatedAt: string;
  pinnedSteps: CaptureStepRecord[];
  activeHandoff?: {
    captureId: string;
    label: string;
    summary: string;
    captureType: CaptureType;
    hasVideo: boolean;
    manifest: CaptureManifest;
  };
  pendingBug?: PendingBugRequest;
}

export interface RestoredHandoffCapture {
  id: string;
  summary: string;
  label: string;
  manifest: CaptureManifest;
  videoBlob?: Blob;
}

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

function newSessionId(): string {
  return `session-${Date.now()}`;
}

function stripManifestForStorage(manifest: CaptureManifest): CaptureManifest {
  return {
    ...manifest,
    screenshot: manifest.captureType === "screenshot" ? "" : manifest.screenshot,
  };
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB open failed"));
  });
}

async function putMedia(key: string, value: Blob): Promise<void> {
  const db = await openDb();

  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB put failed"));
  });

  db.close();
}

async function getMedia(key: string): Promise<Blob | undefined> {
  const db = await openDb();

  const blob = await new Promise<Blob | undefined>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const request = tx.objectStore(STORE_NAME).get(key);
    request.onsuccess = () => resolve(request.result as Blob | undefined);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB get failed"));
  });

  db.close();
  return blob;
}

async function deleteMediaKeys(keys: string[]): Promise<void> {
  if (!keys.length) {
    return;
  }

  const db = await openDb();

  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    for (const key of keys) {
      store.delete(key);
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB delete failed"));
  });

  db.close();
}

function screenshotKey(captureId: string): string {
  return `${captureId}:screenshot`;
}

function videoKey(captureId: string): string {
  return `${captureId}:video`;
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const response = await fetch(dataUrl);
  return response.blob();
}

export async function saveCaptureMedia(
  captureId: string,
  manifest: CaptureManifest,
  videoBlob?: Blob
): Promise<void> {
  if (!isBrowser()) {
    return;
  }

  if (
    manifest.captureType === "screenshot" &&
    manifest.screenshot.startsWith("data:image")
  ) {
    const blob = await dataUrlToBlob(manifest.screenshot);
    await putMedia(screenshotKey(captureId), blob);
  }

  if (videoBlob) {
    await putMedia(videoKey(captureId), videoBlob);
  }
}

export async function loadCaptureMedia(
  captureId: string,
  captureType: CaptureType,
  hasVideo: boolean
): Promise<{ manifestScreenshot?: string; videoBlob?: Blob }> {
  if (!isBrowser()) {
    return {};
  }

  let manifestScreenshot: string | undefined;
  let videoBlob: Blob | undefined;

  if (captureType === "screenshot") {
    const blob = await getMedia(screenshotKey(captureId));
    if (blob) {
      manifestScreenshot = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });
    }
  }

  if (hasVideo) {
    videoBlob = await getMedia(videoKey(captureId));
  }

  return { manifestScreenshot, videoBlob };
}

function readDraftRaw(): DebugCaptureSessionDraft | null {
  if (!isBrowser()) {
    return null;
  }

  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) {
      return null;
    }

    const draft = JSON.parse(raw) as DebugCaptureSessionDraft;
    if (draft.version !== 1) {
      return null;
    }

    const age = Date.now() - new Date(draft.updatedAt).getTime();
    if (age > TTL_MS) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }

    return draft;
  } catch {
    return null;
  }
}

function writeDraft(draft: DebugCaptureSessionDraft): void {
  if (!isBrowser()) {
    return;
  }

  sessionStorage.setItem(
    SESSION_KEY,
    JSON.stringify({
      ...draft,
      updatedAt: new Date().toISOString(),
    })
  );
}

export function loadSessionDraft(): DebugCaptureSessionDraft | null {
  return readDraftRaw();
}

export function hasResumableSessionDraft(): boolean {
  const draft = readDraftRaw();
  if (!draft) {
    return false;
  }

  return Boolean(draft.activeHandoff) || draft.pinnedSteps.length > 0;
}

export async function persistActiveHandoffDraft(input: {
  captureId: string;
  label: string;
  summary: string;
  manifest: CaptureManifest;
  videoBlob?: Blob;
  pendingBug?: PendingBugRequest;
}): Promise<void> {
  if (!isBrowser()) {
    return;
  }

  await saveCaptureMedia(input.captureId, input.manifest, input.videoBlob);

  const existing = readDraftRaw();
  const draft: DebugCaptureSessionDraft = {
    version: 1,
    sessionId: existing?.sessionId ?? newSessionId(),
    updatedAt: new Date().toISOString(),
    pinnedSteps: existing?.pinnedSteps ?? [],
    activeHandoff: {
      captureId: input.captureId,
      label: input.label,
      summary: input.summary,
      captureType: input.manifest.captureType,
      hasVideo: Boolean(input.videoBlob),
      manifest: stripManifestForStorage(input.manifest),
    },
    pendingBug: input.pendingBug ?? existing?.pendingBug,
  };

  writeDraft(draft);
}

export function persistPendingBugDraft(pendingBug: PendingBugRequest): void {
  const draft = readDraftRaw();
  if (!draft?.activeHandoff && !draft?.pinnedSteps.length) {
    return;
  }

  writeDraft({
    ...draft,
    sessionId: draft.sessionId ?? newSessionId(),
    version: 1,
    updatedAt: new Date().toISOString(),
    pinnedSteps: draft.pinnedSteps ?? [],
    pendingBug,
  });
}

export async function pinActiveHandoffStep(label?: string): Promise<boolean> {
  const draft = readDraftRaw();
  if (!draft?.activeHandoff) {
    return false;
  }

  const stepNumber = draft.pinnedSteps.length + 1;
  const stepLabel = label?.trim() || `Step ${stepNumber} (before refresh)`;

  writeDraft({
    ...draft,
    updatedAt: new Date().toISOString(),
    pinnedSteps: [
      ...draft.pinnedSteps,
      {
        stepNumber,
        captureId: draft.activeHandoff.captureId,
        label: stepLabel,
        summary: draft.activeHandoff.summary,
        captureType: draft.activeHandoff.captureType,
        hasVideo: draft.activeHandoff.hasVideo,
        savedAt: new Date().toISOString(),
        manifest: draft.activeHandoff.manifest,
      },
    ],
    activeHandoff: undefined,
    pendingBug: undefined,
  });

  return true;
}

export async function restoreActiveHandoffFromDraft(): Promise<{
  handoff: RestoredHandoffCapture | null;
  pendingBug?: PendingBugRequest;
  pinnedSteps: CaptureStepRecord[];
} | null> {
  const draft = readDraftRaw();
  if (!draft) {
    return null;
  }

  if (!draft.activeHandoff) {
    return {
      handoff: null,
      pendingBug: draft.pendingBug,
      pinnedSteps: draft.pinnedSteps,
    };
  }

  const { manifestScreenshot, videoBlob } = await loadCaptureMedia(
    draft.activeHandoff.captureId,
    draft.activeHandoff.captureType,
    draft.activeHandoff.hasVideo
  );

  const manifest: CaptureManifest = {
    ...draft.activeHandoff.manifest,
    screenshot:
      manifestScreenshot ?? draft.activeHandoff.manifest.screenshot ?? "",
  };

  return {
    handoff: {
      id: draft.activeHandoff.captureId,
      summary: draft.activeHandoff.summary,
      label: draft.activeHandoff.label,
      manifest,
      videoBlob,
    },
    pendingBug: draft.pendingBug,
    pinnedSteps: draft.pinnedSteps,
  };
}

export async function loadPinnedStepMedia(step: CaptureStepRecord): Promise<{
  manifest: CaptureManifest;
  videoBlob?: Blob;
}> {
  const { manifestScreenshot, videoBlob } = await loadCaptureMedia(
    step.captureId,
    step.captureType,
    step.hasVideo
  );

  return {
    manifest: {
      ...step.manifest,
      screenshot: manifestScreenshot ?? step.manifest.screenshot ?? "",
    },
    videoBlob,
  };
}

export async function clearSessionDraft(): Promise<void> {
  if (!isBrowser()) {
    return;
  }

  const draft = readDraftRaw();
  const keys: string[] = [];

  if (draft?.activeHandoff) {
    keys.push(screenshotKey(draft.activeHandoff.captureId));
    if (draft.activeHandoff.hasVideo) {
      keys.push(videoKey(draft.activeHandoff.captureId));
    }
  }

  for (const step of draft?.pinnedSteps ?? []) {
    keys.push(screenshotKey(step.captureId));
    if (step.hasVideo) {
      keys.push(videoKey(step.captureId));
    }
  }

  sessionStorage.removeItem(SESSION_KEY);
  await deleteMediaKeys(keys);
}

export function getPinnedStepsFromDraft(): CaptureStepRecord[] {
  return readDraftRaw()?.pinnedSteps ?? [];
}
